import { Component, EventEmitter, Injector, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import {
  AccountMembership, APIService, CreateContactWithNumberInput,
  MutationIntegrationActionsBodyInput, CreateEntityListInput,
  CreateEntityActionDefinitionInput, CreateDistributionDefinitionInput,
  EntityTypeInList, ListCreationType, EntityListStatus, EntityListType,
  ContactAssignmentStatus, DistributionCreationType, DistributedEntityType,
  AssignmentRulesetInput, AssigneeInput, AssigneeType, Priority, DistributionType,
  Segment
} from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { AILeadDiscoveryService } from '../../../contacts/ai-lead-discovery/ai-lead-discovery.service';
import { AiWorkspaceSetupService } from '../ai-workspace-setup.service';
import { FacebookLoginProvider, SocialAuthService } from '@abacritt/angularx-social-login';
import { AmplifyStorageService } from '../../../../../services/amplify-storage.service';
import { v4 as uuidv4 } from 'uuid';
import { Subscription } from 'rxjs';

interface QuickLead {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  source?: string;
  status?: 'pending' | 'creating' | 'created' | 'error';
}

interface FbPage {
  entityId: string;
  entityType: string;
  userId: string;
  name: string;
  subscribed: boolean;
  linking?: boolean;
}

interface ColumnMapping {
  incomingKey: string;
  mappedKey: string;
  mappedClass: string;
}

@Component({
  selector: 'ws-import-leads',
  templateUrl: './ws-import-leads.component.html',
  imports: [FormsModule, NgClass, MatButton]
})
export class WsImportLeadsComponent implements OnInit, OnDestroy {
  @Input() activeAccount: AccountMembership;
  @Input() invitedUserIds: string[] = [];
  @Input() testMode = false;
  @Input() businessDescription = '';
  @Input() extractedData: any = null;
  @Output() completed = new EventEmitter<void>();

  mode: 'choose' | 'facebook' | 'upload' | 'discover' | 'review' = 'choose';
  leads: QuickLead[] = [];
  isImporting = false;

  // Upload mode
  csvText = '';

  // AI discovery mode
  discoveryPrompt = '';
  isDiscovering = false;

  // Facebook mode
  isConnectingFb = false;
  isFbConnected = false;
  fbPages: FbPage[] = [];
  isLoadingPages = false;

  // Bulk upload mode (file uploads)
  rawFile: File = null;
  rawRows: Record<string, any>[] = [];
  columnMappings: ColumnMapping[] = [];
  totalRowCount = 0;
  isMapping = false;
  uploadSource: 'file' | 'paste' | 'discover' = 'paste';
  detectedColumns: { key: string, mappedTo: string }[] = [];
  private socialAuthService?: SocialAuthService;
  private fbInitStateSubscription?: Subscription;
  private fbAuthStateSubscription?: Subscription;

  private contactModelAutoMatch = [
    { key: 'name', value: 'Full Name', description: 'Name of person' },
    { key: 'department', value: 'Department', description: 'Department at the company or business' },
    { key: 'designation', value: 'Designation', description: 'Designation of the person' },
    { key: 'phoneNumber.0', value: 'Contact Number', description: 'Phone number of the person' },
    { key: 'phoneNumber.1', value: 'Contact Number - Alternate', description: 'Alternate phone number of person' },
    { key: 'emails.0.email', value: 'Email Id', description: 'Email address' },
    { key: 'emails.1.email', value: 'Email Id - Alternate', description: 'Alternate email address' },
    { key: 'addresses.0.rawAddressText', value: 'Full Address', description: 'full address of office or person' },
    { key: 'addresses.0.streetAddress', value: 'Street Address', description: 'street address' },
    { key: 'addresses.0.city', value: 'City', description: 'city' },
    { key: 'addresses.0.state', value: 'State', description: 'state' },
    { key: 'addresses.0.country', value: 'Country', description: 'country' },
    { key: 'addresses.0.zipcode', value: 'Zipcode', description: 'zip code or pin code' },
    { key: 'contactSource', value: 'Contact Source', description: 'Source of the lead or contact' },
    { key: 'contactSourceDrillDownOne', value: 'Contact Source Level Two', description: 'Level one break down of contact source or contact sub-source' },
    { key: 'business.name', value: 'Business Name', description: 'Name of Business' },
    { key: 'business.website', value: 'Business Website', description: 'Business Website' },
    { key: 'business.industry', value: 'Industry', description: 'Industry of the business' },
    { key: 'note', value: 'Note', description: 'default field to map if other field does not match' }
  ];

  constructor(
    private api: APIService,
    private toastr: ToastrService,
    private aiDiscovery: AILeadDiscoveryService,
    private aiService: AiWorkspaceSetupService,
    private storageService: AmplifyStorageService,
    private ngZone: NgZone,
    private injector: Injector,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.cleanupFacebookAuthSubscriptions();
    this.socialAuthService = undefined;
  }

  // --- Option handlers ---
  selectFacebook() {
    this.mode = 'facebook';
    this.checkFbConnection();
  }

  selectUpload() {
    this.mode = 'upload';
  }

  selectDiscover() {
    this.mode = 'discover';
  }

  // --- Facebook mode ---
  private async checkFbConnection() {
    if (!this.activeAccount?.accountId) return;
    this.isLoadingPages = true;
    try {
      const pages = await this.getFbPages();
      if (pages.length > 0) {
        this.isFbConnected = true;
        this.fbPages = pages;
      }
    } catch (e) {
      console.error('Error checking FB connection:', e);
    } finally {
      this.isLoadingPages = false;
    }
  }

  connectFacebook() {
    this.isConnectingFb = true;

    const fbLoginOptions = {
      scope: 'pages_show_list,pages_manage_ads,pages_manage_metadata,pages_read_engagement,leads_retrieval,business_management',
      return_scopes: true,
      enable_profile_selector: true,
    };

    const config = {
      autoLogin: false,
      providers: [
        {
          id: FacebookLoginProvider.PROVIDER_ID,
          provider: new FacebookLoginProvider('914345362883795', fbLoginOptions),
        },
      ],
      onError: (err) => console.error('FB auth error:', err),
    };

    this.cleanupFacebookAuthSubscriptions();
    this.socialAuthService = new SocialAuthService(config as any, this.ngZone, this.injector);
    this.fbInitStateSubscription = this.socialAuthService.initState.subscribe(() => {
      this.socialAuthService?.signIn(FacebookLoginProvider.PROVIDER_ID);
    });

    this.fbAuthStateSubscription = this.socialAuthService.authState.subscribe((user) => {
      if (user?.authToken) {
        this.cleanupFacebookAuthSubscriptions();
        this.sendFbToken(user.authToken);
      } else {
        this.isConnectingFb = false;
      }
    });
  }

  private cleanupFacebookAuthSubscriptions() {
    this.fbInitStateSubscription?.unsubscribe();
    this.fbInitStateSubscription = undefined;
    this.fbAuthStateSubscription?.unsubscribe();
    this.fbAuthStateSubscription = undefined;
  }

  private async sendFbToken(token: string) {
    try {
      const newIntegration = {
        integrationId: 'FACEBOOK',
        authType: 'accessToken',
        accountId: this.activeAccount.accountId,
        createdById: this.activeAccount.userId,
        connectionName: 'FACEBOOK',
        accessToken: token,
      };

      const b = <MutationIntegrationActionsBodyInput>{
        accountId: this.activeAccount.accountId,
        userId: this.activeAccount.userId,
        action: 'CREATE_CONNECTION',
        data: JSON.stringify(newIntegration),
        networkType: 'FACEBOOK',
      };

      const event = await this.api.IntegrationActions(undefined, b);
      if (event) {
        const resp = JSON.parse(event);
        const status = resp?.result;
        if (status?.length > 0 && status[0].tokenStatus) {
          this.toastr.success('Successfully connected with Facebook');
          this.isFbConnected = true;
          this.isLoadingPages = true;
          this.fbPages = await this.getFbPages();
          this.isLoadingPages = false;
        } else {
          this.toastr.error('Error connecting', status?.[0]?.message || 'Unknown error');
        }
      }
    } catch (e: any) {
      console.error('Error connecting with Facebook:', e);
      this.toastr.error('Failed to connect with Facebook');
    } finally {
      this.isConnectingFb = false;
    }
  }

  private async getFbPages(): Promise<FbPage[]> {
    const b = <MutationIntegrationActionsBodyInput>{
      accountId: this.activeAccount.accountId,
      action: 'GET_CONNECTION',
      data: '',
      networkType: '',
    };

    const event = await this.api.IntegrationActions(undefined, b);
    if (!event) return [];

    const resp = JSON.parse(event);
    const results = resp?.result || [];
    const fbResult = results.find((r: any) => r.networkType === 'FACEBOOK');
    if (!fbResult?.connected) return [];

    return fbResult.connected.map((page: any) => ({
      entityId: page.entityId,
      entityType: page.entityType,
      userId: page.userId,
      name: page.name || page.entityId,
      subscribed: page.subscribed === true,
      linking: false,
    }));
  }

  async togglePageLink(page: FbPage) {
    if (page.linking) return;
    page.linking = true;

    try {
      const action = page.subscribed ? 'UNSUBSCRIBE_ENTITY' : 'SUBSCRIBE_ENTITY';
      const data = { entityId: page.entityId, entityType: page.entityType, userId: page.userId };

      const b = <MutationIntegrationActionsBodyInput>{
        accountId: this.activeAccount.accountId,
        userId: this.activeAccount.userId,
        action,
        data: JSON.stringify(data),
        networkType: 'FACEBOOK',
      };

      await this.api.IntegrationActions(undefined, b);
      page.subscribed = !page.subscribed;
      this.toastr.success(page.subscribed ? `Linked "${page.name}"` : `Unlinked "${page.name}"`);
    } catch (e: any) {
      console.error('Error toggling page link:', e);
      this.toastr.error('Failed to update page link');
    } finally {
      page.linking = false;
    }
  }

  get linkedPagesCount(): number {
    return this.fbPages.filter(p => p.subscribed).length;
  }

  fbDone() {
    this.completed.emit();
  }

  // =====================================================
  // --- Upload mode: File parsing with LLM auto-mapping
  // =====================================================
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    this.rawFile = file;
    const isExcel = /\.xlsx?$/i.test(file.name);

    if (isExcel) {
      this.parseExcelFile(file);
    } else {
      this.parseCsvFile(file);
    }
    input.value = '';
  }

  private async parseExcelFile(file: File) {
    this.isMapping = true;
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

      // Parse as JSON objects — first row used as keys automatically
      const allRows: Record<string, any>[] = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

      if (allRows.length === 0) {
        this.toastr.warning('The spreadsheet is empty');
        this.isMapping = false;
        return;
      }

      const MAX_ROWS = 100;
      this.totalRowCount = allRows.length;
      this.rawRows = allRows.slice(0, MAX_ROWS);

      await this.autoMapColumns();
    } catch (e: any) {
      console.error('Error parsing Excel file:', e);
      this.toastr.error('Failed to read the spreadsheet.');
      this.isMapping = false;
    }
  }

  private parseCsvFile(file: File) {
    this.isMapping = true;
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      try {
        const text = e.target.result as string;
        const lines = text.trim().split('\n');
        if (lines.length < 2) {
          this.toastr.warning('The file appears empty or has only one row');
          this.isMapping = false;
          return;
        }

        // Use first line as headers
        const headers = lines[0].split(/[,\t]/).map(h => h.trim().replace(/^["']|["']$/g, ''));
        const MAX_ROWS = 100;
        const allRows: Record<string, any>[] = [];

        for (let i = 1; i < lines.length; i++) {
          const parts = lines[i].split(/[,\t]/).map(p => p.trim().replace(/^["']|["']$/g, ''));
          if (parts.every(p => !p)) continue; // skip empty rows
          const row: Record<string, any> = {};
          headers.forEach((h, idx) => {
            row[h || `Column ${idx + 1}`] = parts[idx] || '';
          });
          allRows.push(row);
        }

        this.totalRowCount = allRows.length;
        this.rawRows = allRows.slice(0, MAX_ROWS);

        await this.autoMapColumns();
      } catch (err: any) {
        console.error('Error parsing CSV file:', err);
        this.toastr.error('Failed to read the CSV file.');
        this.isMapping = false;
      }
    };
    reader.readAsText(file);
  }

  private async autoMapColumns() {
    if (this.rawRows.length === 0) {
      this.isMapping = false;
      return;
    }

    try {
      // Send 2-3 sample rows to LLM for column detection
      const sampleRows = this.rawRows.slice(0, Math.min(3, this.rawRows.length));
      const response = await this.api.MapDataFields(
        JSON.stringify(sampleRows),
        JSON.stringify(this.contactModelAutoMatch)
      );

      if (!response) {
        this.toastr.warning('Could not auto-detect columns. Please try a different file.');
        this.isMapping = false;
        return;
      }

      const parsed = JSON.parse(response);
      if (parsed.statusCode !== 200 || !parsed.body) {
        this.toastr.warning('Could not auto-detect columns.');
        this.isMapping = false;
        return;
      }

      const body = Array.isArray(parsed.body) ? parsed.body : JSON.parse(parsed.body);

      // Build column mappings from AI response
      this.columnMappings = [];
      for (const mapping of body) {
        const keys = Object.keys(mapping);
        if (!keys.length || !mapping[keys[0]]) continue;

        const incomingKey = keys[0];
        const mappedValue = mapping[incomingKey];
        const modelEntry = this.contactModelAutoMatch.find(
          m => m.key === mappedValue || m.value === mappedValue
        );

        if (modelEntry) {
          this.columnMappings.push({
            incomingKey,
            mappedKey: modelEntry.key,
            mappedClass: 'Contact',
          });
        }
      }

      // Check if phone number was mapped
      const hasPhone = this.columnMappings.some(m => m.mappedKey.startsWith('phoneNumber'));
      if (!hasPhone) {
        this.toastr.warning('No phone number column detected. Please upload a file with phone numbers.');
        this.isMapping = false;
        return;
      }

      // Build detected columns summary for display (exclude 'note')
      this.detectedColumns = this.columnMappings
        .filter(m => m.mappedKey !== 'note')
        .map(m => ({
          key: m.incomingKey,
          mappedTo: this.contactModelAutoMatch.find(cm => cm.key === m.mappedKey)?.value || m.mappedKey,
        }));

      // Generate preview leads from mapped data
      const nameMapping = this.columnMappings.find(m => m.mappedKey === 'name');
      const phoneMapping = this.columnMappings.find(m => m.mappedKey === 'phoneNumber.0');
      const emailMapping = this.columnMappings.find(m => m.mappedKey === 'emails.0.email');
      const addressMapping = this.columnMappings.find(m => m.mappedKey === 'addresses.0.rawAddressText');

      this.leads = this.rawRows.slice(0, 10).map(row => ({
        name: nameMapping ? String(row[nameMapping.incomingKey] || '').trim() : '',
        phone: phoneMapping ? String(row[phoneMapping.incomingKey] || '').trim() : '',
        email: emailMapping ? String(row[emailMapping.incomingKey] || '').trim() : '',
        address: addressMapping ? String(row[addressMapping.incomingKey] || '').trim() : '',
        status: 'pending' as const,
      })).filter(l => l.phone);

      this.uploadSource = 'file';
      this.mode = 'review';

      const cappedCount = Math.min(this.totalRowCount, 100);
      const msg = this.totalRowCount > 100
        ? `${this.totalRowCount} leads found. First 100 will be imported now — you can import more from Contacts later.`
        : `${this.totalRowCount} lead(s) found in your file`;
      this.toastr.success(msg);
    } catch (e: any) {
      console.error('Error in auto-mapping:', e);
      this.toastr.warning('Could not auto-detect columns. Please try a different file.');
    } finally {
      this.isMapping = false;
    }
  }

  // =====================================================
  // --- Paste mode: Simple name, phone parsing (no LLM)
  // =====================================================
  parseCsv() {
    if (!this.csvText.trim()) return;
    const lines = this.csvText.trim().split('\n');
    const MAX_LEADS = 100;
    const parsed: QuickLead[] = [];

    for (const line of lines) {
      if (parsed.length >= MAX_LEADS) break;
      const parts = line.split(/[,\t]/).map(p => p.trim().replace(/^["']|["']$/g, ''));
      if (parts.length >= 2) {
        parsed.push({ name: parts[0], phone: parts[1], status: 'pending' });
      } else if (parts.length === 1 && parts[0]) {
        parsed.push({ name: '', phone: parts[0], status: 'pending' });
      }
    }
    this.csvText = '';
    if (parsed.length > 0) {
      this.leads = parsed;
      this.uploadSource = 'paste';
      this.mode = 'review';
      const msg = lines.length > MAX_LEADS
        ? `Showing first ${MAX_LEADS} of ${lines.length} leads. You can import more later from Contacts.`
        : `${parsed.length} lead(s) parsed`;
      this.toastr.success(msg);
    } else {
      this.toastr.warning('No valid data found. Make sure your data has phone numbers.');
    }
  }

  // =====================================================
  // --- AI Discovery mode
  // =====================================================
  async discoverLeads() {
    this.isDiscovering = true;

    try {
      // Build a description combining business context + user prompt
      const desc = this.businessDescription || this.discoveryPrompt.trim() || 'general business';

      const results = await this.aiService.discoverTestLeads(desc, this.extractedData);

      if (results.length === 0) {
        this.toastr.info('No leads found. Try editing the description and searching again.');
        return;
      }

      this.leads = results.map(r => ({
        name: r.business_name,
        phone: r.phone,
        email: r.email || '',
        address: r.address || '',
        source: 'AI Test Lead Finder',
        status: 'pending' as const,
      }));
      this.uploadSource = 'discover';
      this.mode = 'review';
      this.toastr.success(`${results.length} test lead(s) found for your business`);
    } catch (e: any) {
      console.error('Error discovering leads:', e);
      this.toastr.error(e?.message || 'Failed to discover leads. Try again.');
    } finally {
      this.isDiscovering = false;
    }
  }

  // =====================================================
  // --- Review mode
  // =====================================================
  removeLead(lead: QuickLead) {
    this.leads = this.leads.filter(l => l !== lead);
  }

  async importLeads() {
    if (this.testMode) {
      const count = this.uploadSource === 'file' ? Math.min(this.totalRowCount, 100) : this.leads.length;
      this.toastr.success(`Test mode: ${count} lead(s) would be imported`);
      this.completed.emit();
      return;
    }

    if (this.uploadSource === 'file') {
      await this.submitBulkUpload();
    } else {
      await this.importLeadsIndividually();
    }
  }

  // --- Individual creates (paste / AI discovery) ---
  private async importLeadsIndividually() {
    const pending = this.leads.filter(l => l.status === 'pending');
    if (pending.length === 0) {
      this.completed.emit();
      return;
    }
    this.isImporting = true;

    let created = 0;
    let failed = 0;

    for (const lead of pending) {
      lead.status = 'creating';
      try {
        const phone = this.aiDiscovery.toE164(lead.phone);
        if (!phone) {
          lead.status = 'error';
          failed++;
          continue;
        }

        const input = <CreateContactWithNumberInput>{
          createContactInput: {
            accountId: this.activeAccount.accountId,
            name: lead.name || 'Unknown',
            addedById: this.activeAccount.userId,
            ownerId: this.activeAccount.userId,
            contactSource: lead.source || 'MANUAL',
          },
          contactNumbers: [phone],
        };
        await this.api.CreateContactWithNumber(input);
        lead.status = 'created';
        created++;
      } catch (e: any) {
        lead.status = 'error';
        failed++;
        console.error('Error creating contact:', e);
      }
    }

    if (created > 0) this.toastr.success(`${created} lead(s) created`);
    if (failed > 0) this.toastr.warning(`${failed} lead(s) failed`);

    this.isImporting = false;
    if (failed === 0) {
      this.completed.emit();
    }
  }

  // --- Bulk upload pipeline (file uploads) ---
  private async submitBulkUpload() {
    this.isImporting = true;

    try {
      const XLSX = await import('xlsx');
      const timestamp = Date.now();
      const accountId = this.activeAccount.accountId;
      const userId = this.activeAccount.userId;

      // 1. Transform raw rows using AI mappings
      const mappedData = this.rawRows.map(row => {
        const mapped: Record<string, any> = {};
        for (const col of this.columnMappings) {
          const val = row[col.incomingKey];
          if (val === undefined || val === null || val === '') continue;
          if (col.mappedKey === 'note' && mapped['note']) {
            mapped['note'] += '\n' + col.incomingKey + ': ' + val;
          } else {
            mapped[col.mappedKey] = String(val);
          }
        }
        return mapped;
      });

      // 2. Convert mapped data to CSV
      const worksheet = XLSX.utils.json_to_sheet(mappedData, { dateNF: 'dd/mm/yyyy' });
      const csvOutput = XLSX.utils.sheet_to_csv(worksheet, { forceQuotes: true });

      // 3. Upload raw file to S3
      const rawFileName = `${accountId}/${userId}/${timestamp}-${this.rawFile.name}`;
      await this.storageService.uploadFileToS3(
        this.rawFile, rawFileName, 'public',
        this.rawFile.type || 'application/octet-stream'
      );

      // 4. Upload mapped CSV to S3
      const mappedFileName = `${accountId}/${userId}/${timestamp}-mapped.csv`;
      const csvBlob = new Blob([csvOutput], { type: 'text/csv' });
      await this.storageService.uploadFileToS3(csvBlob, mappedFileName, 'public', 'text/csv');

      // 5. Determine assignment
      const entityListId = uuidv4();
      const entityActionId = uuidv4();

      const listPayload = <CreateEntityListInput>{
        id: entityListId,
        listName: 'Wizard Import - ' + new Date().toLocaleDateString(),
        description: 'Quick import from workspace setup',
        entityTypeInList: EntityTypeInList.CONTACT,
        accountId,
        listCreationType: ListCreationType.BULK_UPLOAD,
        status: EntityListStatus.UNDER_PROCESSING,
        ownerId: userId,
        entityListType: EntityListType.STATIC,
        rawBulkUploadFileRef: rawFileName,
        modifiedBulkUploadFileRef: mappedFileName,
        entityActionDefinitionReference: entityActionId,
      };

      const assignees = (this.invitedUserIds || []).filter(id => id !== userId);

      if (assignees.length > 1) {
        // Round-robin distribution across team members
        const distInput = <CreateDistributionDefinitionInput>{
          accountId,
          ownerId: userId,
          name: new Date().toISOString(),
          distributionCreationType: DistributionCreationType.SYSTEM,
          description: 'Wizard Import Round Robin',
          distributedEntityType: [DistributedEntityType.CONTACT],
          rule: [<AssignmentRulesetInput>{
            assigneeSet: assignees.map(id => (<AssigneeInput>{
              assigneeId: id,
              assigneeType: AssigneeType.USER,
              priority: Priority.MEDIUM,
              maxLoading: 1000,
            })),
            distributionType: DistributionType.ROUND_ROBIN,
          }],
        };
        const dist: any = await this.api.CreateDistributionDefinition(distInput);
        if (dist?.id) {
          listPayload.associatedDistributionId = dist.id;
          listPayload.toBeAssigned = ContactAssignmentStatus.UNASSIGNED;
        }
      } else if (assignees.length === 1) {
        listPayload.assignedUserId = assignees[0];
        listPayload.toBeAssigned = ContactAssignmentStatus.ASSIGNED;
      } else {
        // No team members — assign to self (superadmin)
        listPayload.assignedUserId = userId;
        listPayload.toBeAssigned = ContactAssignmentStatus.ASSIGNED;
      }

      // 6. Build EntityActionDefinition with column mappings
      const columnKeys = Object.keys(this.rawRows[0] || {});
      const mappings = columnKeys.map(key => {
        const mapping = this.columnMappings.find(m => m.incomingKey === key);
        return {
          name: key,
          mappedClass: mapping ? mapping.mappedClass : null,
          mappedProperty: mapping ? mapping.mappedKey : null,
          updateable: false,
        };
      });

      const actionPayload = <CreateEntityActionDefinitionInput>{
        id: entityActionId,
        accountId,
        ownerId: userId,
        action: 'BULK_UPLOAD',
        name: `Wizard Import - ${new Date().toDateString()}`,
        subscriptionType: 'ADHOC',
        status: 'REQUESTED',
        createdBy: userId,
        updatedBy: userId,
        segment: <Segment>{},
        target: JSON.stringify({ mappings }),
      };

      // 7. Create action + list in parallel
      await Promise.all([
        this.api.CreateEntityActionDefinition(actionPayload),
        this.api.CreateEntityList(listPayload),
      ]);

      this.toastr.success('Your leads are being imported! This may take a few minutes.');
      this.isImporting = false;
      this.completed.emit();
    } catch (e: any) {
      console.error('Error submitting bulk upload:', e);
      this.toastr.error('Failed to submit import. Please try again.');
      this.isImporting = false;
    }
  }

  backToChoose() {
    this.mode = 'choose';
    this.leads = [];
    this.csvText = '';
    this.rawFile = null;
    this.rawRows = [];
    this.columnMappings = [];
    this.detectedColumns = [];
    this.totalRowCount = 0;
    this.uploadSource = 'paste';
  }

  skip() {
    this.completed.emit();
  }
}
